import { z } from 'zod';

/**
 * Rollout du Fil (les « nouveautés » du vendeur). `off` = personne, admins
 * compris — la feature ne s'allume jamais toute seule ; `admin_only` = les
 * admins testent en prod ; `everyone` = tous les rôles. Appliqué côté API
 * (StoriesService), l'UI ne fait que suivre.
 */
export const StoriesVisibilitySchema = z.enum(['off', 'admin_only', 'everyone']);
export type StoriesVisibility = z.infer<typeof StoriesVisibilitySchema>;

/**
 * Le Fil est-il ouvert à ce rôle ? UNE seule implémentation, consommée par la
 * garde API (StoriesService) et par l'UI (anneau, menu, écran) — sinon les
 * deux dérivent et l'UI montre une entrée qui finit en 403.
 */
export function storiesEnabledFor(visibility: StoriesVisibility, role: string | undefined): boolean {
  if (!role) return false;
  if (visibility === 'everyone') return true;
  return visibility === 'admin_only' && role === 'admin';
}

/**
 * Ce qui devient une story. Chaque valeur est DÉRIVÉE d'un `LeadEvent` que
 * l'acteur a déjà le droit de voir — le Fil ne crée aucune information.
 *  - `sale_confirmed` / `sale_validated` / `delivered` : transitions de statut ;
 *  - `to_contact` : qualification callcenter promue « injoignable / messagerie /
 *    non contacté » sur une fiche encore en cours — le vendeur peut prévenir ;
 *  - `callback_requested` : le prospect a demandé à être rappelé.
 */
export const STORY_KINDS = [
  'sale_confirmed',
  'sale_validated',
  'delivered',
  'to_contact',
  'callback_requested',
] as const;
export const StoryKindSchema = z.enum(STORY_KINDS);
export type StoryKind = z.infer<typeof StoryKindSchema>;

export const StoryLeadSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  /** Le vendeur peut appeler ou écrire au prospect depuis la story. */
  phone: z.string().nullable(),
  city: z.string().nullable(),
  /** Date de la visite (création de la fiche), ISO. */
  visitedAt: z.string(),
  verticaleName: z.string().nullable(),
  /** Slug + icône : la story reprend le style des tuiles de verticale du wizard. */
  verticaleSlug: z.string().nullable(),
  verticaleIcon: z.string().nullable(),
  /** Marque affichable au vendeur (`brand`, sinon `name` sans le call-center). */
  partnerBrand: z.string().nullable(),
  /** Moment du transfert au partenaire (ISO), même ancre que l'event
   *  `delivered` : créneau passé, sinon `completedAt`. Null tant que la fiche
   *  n'est pas partie. Sert à « livrée il y a 2 h à Club Conso ». */
  deliveredAt: z.string().nullable(),
});
export type StoryLead = z.infer<typeof StoryLeadSchema>;

export const StorySchema = z.object({
  /** `${kind}:${leadId}` — UNE story par fiche : le dernier fait l'emporte, et
   *  l'id change avec lui (une vente après un « à prévenir » = nouvelle story). */
  id: z.string(),
  kind: StoryKindSchema,
  /** ISO — le moment du fait, pas celui de la lecture. Avance quand le fait
   *  se répète (nouvelle tentative) : c'est ce qui rallume l'anneau. */
  at: z.string(),
  leadId: z.string(),
  lead: StoryLeadSchema,
  detail: z
    .object({
      /** Nombre de tentatives du centre d'appel — `meta.attemptNumber` du CRM
       *  quand il est fourni, sinon le nombre de qualifications vues. */
      attempts: z.number().int().optional(),
      /** Sous-groupe Hipto de la dernière qualif (« Relance »…). */
      lastSubMetric: z.string().nullable().optional(),
      /** Numéro miroir du partenaire, pour la story « à prévenir ». */
      mirrorPhone: z.string().nullable().optional(),
      /** Horaires du centre d'appel, déjà rédigés pour un SMS (« du lundi au
       *  vendredi de 9h à 19h »). Null si le partenaire n'en a pas. */
      openingHours: z.string().nullable().optional(),
      /** SMS préétablis déjà préparés par le vendeur pour cette fiche
       *  (`vendor_sms_prepared`, toute période). Sert au « il t'en reste N ». */
      vendorSmsSent: z.number().int().optional(),
    })
    .optional(),
});
export type Story = z.infer<typeof StorySchema>;

export const StoriesResponseSchema = z.object({
  stories: z.array(StorySchema),
  /** Début de la fenêtre RÉELLEMENT servie (ISO) : 7 jours, ou moins si le
   *  plafond de lecture a été atteint — auquel cas `truncated` est vrai. */
  since: z.string(),
  truncated: z.boolean().optional(),
  generatedAt: z.string(),
});
export type StoriesResponse = z.infer<typeof StoriesResponseSchema>;
